import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  LogOut,
  Loader2,
  MessagesSquare,
  Users,
  Share2,
  MessageSquare,
  Activity,
  ArrowLeft,
  MapPin,
  Globe,
} from "lucide-react";
import Logo from "@/components/Logo";
import {
  adminDaily,
  adminLogout,
  adminMe,
  adminQuestions,
  adminStats,
  adminTopQuestions,
} from "@/lib/adminApi";
import { api } from "@/lib/api";

async function fetchEpcStats() {
  const { data } = await api.get("/admin/epc-stats");
  return data;
}
async function fetchTopPostcodes() {
  const { data } = await api.get("/admin/top-postcodes", { params: { limit: 10 } });
  return data;
}
async function fetchRecentLookups() {
  const { data } = await api.get("/admin/recent-lookups", { params: { limit: 25 } });
  return data;
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [top, setTop] = useState([]);
  const [daily, setDaily] = useState([]);
  const [epcStats, setEpcStats] = useState(null);
  const [topPostcodes, setTopPostcodes] = useState([]);
  const [recentLookups, setRecentLookups] = useState([]);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    try {
      const [m, s, q, t, d, es, tp, rl] = await Promise.all([
        adminMe(),
        adminStats(),
        adminQuestions(50),
        adminTopQuestions(10),
        adminDaily(14),
        fetchEpcStats(),
        fetchTopPostcodes(),
        fetchRecentLookups(),
      ]);
      setMe(m);
      setStats(s);
      setQuestions(q);
      setTop(t);
      setDaily(d);
      setEpcStats(es);
      setTopPostcodes(tp);
      setRecentLookups(rl);
    } catch (err) {
      if (err?.response?.status === 401) {
        nav("/admin/login", { replace: true });
      } else {
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, []);

  async function handleLogout() {
    try {
      await adminLogout();
    } finally {
      nav("/admin/login", { replace: true });
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground font-sans" data-testid="admin-dashboard">
      <header className="border-b border-border bg-white/70 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center gap-3 px-6 h-14">
          <Link to="/" className="flex items-center gap-2">
            <Logo className="h-7 w-7" />
            <span className="font-heading text-sm tracking-tight">chatepc<span className="text-epc-a">.co.uk</span></span>
          </Link>
          <div className="text-xs uppercase tracking-widest text-muted-foreground ml-3 border-l border-border pl-3 hidden md:block">
            Admin dashboard
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-muted-foreground hidden md:block">{me?.email}</span>
            <Link to="/chat" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <ArrowLeft className="h-3.5 w-3.5" /> Site
            </Link>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              data-testid="admin-logout"
              className="rounded-none"
            >
              <LogOut className="h-4 w-4 md:mr-1.5" />
              <span className="hidden md:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-10">
        {/* Stat cards */}
        <section>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Overview</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
            <StatCard
              icon={<Users className="h-4 w-4" />}
              label="Unique visitors"
              value={stats.unique_users_total}
              sub={`${stats.unique_users_7d} in last 7 days`}
              accent="bg-epc-a"
              testId="stat-users"
            />
            <StatCard
              icon={<MessagesSquare className="h-4 w-4" />}
              label="Total chats"
              value={stats.total_sessions}
              sub={`${stats.sessions_24h} in last 24h`}
              accent="bg-epc-c"
              testId="stat-sessions"
            />
            <StatCard
              icon={<MessageSquare className="h-4 w-4" />}
              label="User messages"
              value={stats.user_messages}
              sub={`${stats.user_messages_24h} in last 24h`}
              accent="bg-epc-d"
              testId="stat-user-msgs"
            />
            <StatCard
              icon={<Share2 className="h-4 w-4" />}
              label="Shared / embed"
              value={`${stats.shared_sessions} / ${stats.embed_sessions}`}
              sub="public link / widget"
              accent="bg-epc-b"
              testId="stat-share-embed"
            />
          </div>
        </section>

        {/* Daily chart */}
        <section>
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Activity</div>
              <h2 className="mt-1 font-heading text-2xl tracking-tight">User messages, last 14 days</h2>
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Activity className="h-3.5 w-3.5" /> auto-refreshes every 30s
            </div>
          </div>
          <DailyChart data={daily} />
        </section>

        {/* EPC Register lookups */}
        <section>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">EPC Register lookups</div>
          <h2 className="mt-1 font-heading text-2xl tracking-tight">Who's using the EPC checker</h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <StatCard
              icon={<MapPin className="h-4 w-4" />}
              label="EPC lookups"
              value={epcStats?.total ?? 0}
              sub={`${epcStats?.last_24h ?? 0} in last 24h`}
              accent="bg-epc-a"
              testId="stat-epc-total"
            />
            <StatCard
              icon={<Activity className="h-4 w-4" />}
              label="Last 7 days"
              value={epcStats?.last_7d ?? 0}
              sub="checks performed"
              accent="bg-epc-c"
              testId="stat-epc-7d"
            />
            <StatCard
              icon={<Globe className="h-4 w-4" />}
              label="England & Wales"
              value={epcStats?.by_region?.ew ?? 0}
              sub={`Scotland ${epcStats?.by_region?.scotland ?? 0} · NI ${epcStats?.by_region?.ni ?? 0}`}
              accent="bg-epc-d"
              testId="stat-epc-regions"
            />
            <StatCard
              icon={<Share2 className="h-4 w-4" />}
              label="By source"
              value={(epcStats?.by_source?.[0]?.source) || "—"}
              sub={epcStats?.by_source?.slice(0, 3).map(s => `${s.source}:${s.count}`).join(" · ") || "no data yet"}
              accent="bg-epc-b"
              testId="stat-epc-sources"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div className="border border-border" data-testid="epc-top-postcodes">
              <div className="px-4 py-3 border-b border-border flex items-baseline justify-between">
                <h3 className="font-heading tracking-tight">Top postcodes</h3>
                <span className="text-xs text-muted-foreground">most searched</span>
              </div>
              {topPostcodes.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">No lookups yet.</div>
              ) : (
                <ul className="divide-y divide-border">
                  {topPostcodes.map((row, i) => (
                    <li key={i} className="flex items-center gap-3 px-4 py-3">
                      <span className="text-xs w-6 text-muted-foreground tabular-nums">{i + 1}</span>
                      <span className="flex-1 font-mono text-sm">{row.postcode}</span>
                      <span className="text-xs text-muted-foreground uppercase tracking-widest">{row.region || "—"}</span>
                      <span className="font-mono text-xs bg-neutral-100 border border-border px-2 py-0.5 ml-2">{row.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="border border-border" data-testid="epc-recent-lookups">
              <div className="px-4 py-3 border-b border-border flex items-baseline justify-between">
                <h3 className="font-heading tracking-tight">Recent lookups</h3>
                <span className="text-xs text-muted-foreground">latest 25</span>
              </div>
              {recentLookups.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">No lookups yet.</div>
              ) : (
                <ul className="divide-y divide-border max-h-[420px] overflow-y-auto">
                  {recentLookups.map((row, i) => (
                    <li key={i} className="px-4 py-3">
                      <div className="text-[11px] text-muted-foreground font-mono">
                        {(row.created_at || "").slice(0, 16).replace("T", " ")} UTC · {row.source}
                        {row.parent_host ? ` · ${row.parent_host}` : ""}
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-sm">
                        {row.house_number && (
                          <span className="font-medium" data-testid={`lookup-house-${i}`}>{row.house_number}</span>
                        )}
                        <span className="font-mono">{row.postcode}</span>
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{row.region}</span>
                        <span className="ml-auto text-xs text-muted-foreground">{row.result_count} result{row.result_count === 1 ? "" : "s"}</span>
                      </div>
                      {row.house_number && (
                        <div className="mt-0.5 text-xs text-muted-foreground truncate" data-testid={`lookup-matched-${i}`}>
                          {row.house_matched ? `Matched: ${row.matched_address}` : "No exact property match"}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>

        {/* Top questions + recent */}
        <section className="grid md:grid-cols-2 gap-6">
          <div className="border border-border">
            <div className="px-4 py-3 border-b border-border flex items-baseline justify-between">
              <h3 className="font-heading tracking-tight">Top asked</h3>
              <span className="text-xs text-muted-foreground">by count</span>
            </div>
            {top.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">No data yet.</div>
            ) : (
              <ul className="divide-y divide-border">
                {top.map((row, i) => (
                  <li key={i} className="flex items-center gap-3 px-4 py-3" data-testid={`top-q-${i}`}>
                    <span className="text-xs w-6 text-muted-foreground tabular-nums">{i + 1}</span>
                    <span className="flex-1 text-sm truncate">{row.question}</span>
                    <span className="font-mono text-xs bg-neutral-100 border border-border px-2 py-0.5">
                      {row.count}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border border-border">
            <div className="px-4 py-3 border-b border-border flex items-baseline justify-between">
              <h3 className="font-heading tracking-tight">Recent questions</h3>
              <span className="text-xs text-muted-foreground">latest 50</span>
            </div>
            {questions.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">No questions yet.</div>
            ) : (
              <ul className="divide-y divide-border max-h-[520px] overflow-y-auto">
                {questions.map((q, i) => (
                  <li key={i} className="px-4 py-3" data-testid={`recent-q-${i}`}>
                    <div className="text-[11px] text-muted-foreground font-mono">
                      {(q.created_at || "").slice(0, 16).replace("T", " ")} UTC
                    </div>
                    <div className="mt-0.5 text-sm">{q.content}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, sub, accent, testId }) {
  return (
    <div className="border border-border p-4" data-testid={testId}>
      <div className={`inline-flex items-center gap-1.5 ${accent} text-black text-[11px] uppercase tracking-widest px-2 py-1`}>
        {icon}
        {label}
      </div>
      <div className="mt-4 font-heading text-3xl tracking-tight">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{sub}</div>
    </div>
  );
}

function DailyChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="mt-4 border border-border p-6 text-sm text-muted-foreground">
        No activity yet.
      </div>
    );
  }
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="mt-4 border border-border p-4">
      <div className="flex items-end gap-2 h-40" data-testid="daily-chart">
        {data.map((d) => (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full flex items-end h-32">
              <div
                className="w-full bg-epc-a transition-all"
                style={{ height: `${(d.count / max) * 100}%` }}
                title={`${d.date}: ${d.count}`}
              />
            </div>
            <div className="text-[10px] text-muted-foreground font-mono">
              {d.date.slice(5)}
            </div>
            <div className="text-[10px] font-medium tabular-nums">{d.count}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
